// GraphQL field declaration for the activeOrders query, passed directly into useDataModel's
// `dataModel` option — mirrors purchaseHistoryDataModel.ts's structure. Originally grounded only
// against the Jira OOP-3943 sample; the response selection below is now confirmed against the real
// staging schema (2026-08-12), which validated the `activeOrders` root field, the `HeaderExtras` and
// `ActiveOrdersRequest!` types, and every field still selected here.
//
// Seven fields were rejected as FieldUndefined by that same staging schema and are therefore NOT
// selected below: `ActiveDistribution.workOrderNumber`/`trackingNumber`,
// `ActiveOrdersLocation.addressLine2`, `ActiveOrdersInstructions.additionalInstructions`,
// `ActiveOrdersLineItem.imageUrl`, and BOTH fields of `ActiveOrdersRecipient` (`name`,
// `phoneNumber`). Because the recipient type had no surviving field, the whole `recipient` block is
// dropped — a field with an empty selection set is itself invalid GraphQL.
//
// IMPORTANT — this is a selection-set-only removal, deliberately narrower than the
// purchaseHistory/orderDetails precedent in ADR-002, which also deleted its rejected fields from
// src/data/types.ts and the adapters. All seven remain declared (and optional) on
// RawActiveDistribution & friends in types.ts, mapActiveDistributionToRow still reads them
// defensively, and mock-data/active-orders-response.ts still supplies them — per direct product
// direction (2026-08-12) that these values DO come back in some cases. So the moment a schema that
// exposes them is targeted, only this file needs to change; nothing downstream has to be rebuilt.
//
// There is no way to make a selection "optional" in GraphQL — a selected field that is not in the
// schema fails validation for the ENTIRE document, and @skip/@include do not help (they still
// require the field to exist). So "tolerate it when present" can only live in the TypeScript
// types/adapters, never in the query. Until the real names are confirmed by introspection (see
// ADR-003 Future work), the Pickup Person / Contact Number columns render their '—' placeholder, the
// Track Delivery row action does not appear, and rows carry no line-item thumbnails.
//
// orderOrigin/storeNumber/transactionId/registerNumber/salesDate were a SEPARATE, LATER addition
// (OOP-4197, added purely so an active-orders row would carry enough identifying data to build an
// orderDetails-v2 request — see src/data/adapters/fulfillmentDrawer.ts's
// buildActiveOrderDetailsParams). All five were REJECTED as FieldUndefined by the real stage
// schema (2026-09-17) — `ActiveDistribution` has none of them — confirmed by a live stage error
// report, not merely predicted, and REMOVED from the selection below (this is exactly the "whole
// activeOrders query fails for every consumer" failure mode the previous revision of this comment
// warned about, and it happened). Same selection-set-only-removal treatment as the seven fields
// above: `RawActiveDistribution` in types.ts keeps all five as optional/nullable, and
// mock-data/active-orders-response.ts still supplies them for buildActiveOrderDetailsParams' own
// unit tests — but a REAL activeOrders response will never populate them, so
// buildActiveOrderDetailsParams' 'inStore' branch is effectively unreachable in production until
// a schema that exposes these (or equivalents) is targeted; `orderOrigin` being permanently absent
// also means that function's 'online' variant always falls through to its orderNumber-prefix
// regex fallback, never the orderOrigin check. See ADR-003's OOP-4197 addendum.
//
// NOTE: a failing selection set takes the Deliveries/Pickups toggle down with it —
// ActiveOrdersTable returns [] for fulfillmentFilterOptions on any non-success status, and the
// presentational shell gates the toggle on that array being non-empty. The toggle vanishing is a
// symptom of query failure, not a bug in the toggle.
//
// Two structural differences from purchaseHistory, both confirmed by the sample: there is no
// top-level identity argument (no `userId` alongside `request`, unlike purchaseHistory) —
// `customerAccountId`/`purchaserId` both live inside `request`; and `filters` entries are
// singular `{name, value}`, not `{name, values: []}`.
//
// THE REQUEST CONTRACT (confirmed 2026-08-12). `ActiveOrdersRequest` takes exactly:
// customerAccountId, purchaserId, pageSize, pageNumber, and filters. Notably:
//
// * There are NO startDate/endDate fields. This API expresses a date window ONLY through the
//   `{name: 'distributionDate'}` filter sentinel — one of 'ALL' | 'Today' | 'Tomorrow' |
//   'Next 7 Days' (exact casing/spacing; see ActiveOrdersDateSentinel below). An earlier revision of
//   this file sent startDate/endDate instead and dropped the sentinel; both were wrong, and are now
//   reversed. There is consequently no way to request an arbitrary date range — see
//   ActiveOrdersTable.tsx for how DateRangeFilter's 'custom' flow is handled, and ADR-003 for the
//   open product question that creates.
// * `{name: 'distributionType'}` takes 'ALL' | 'DELIVERY' | 'PICKUP' (uppercase).
//
// searchValue/sortBy/sortOrder are NOT in that contract and are no longer sent (2026-09-21):
// order-management-svc's schema.graphqls defines `ActiveOrdersRequest` with exactly
// customerAccountId/purchaserId/pageSize/pageNumber/filters — confirmed directly against the OMS
// source, not staging introspection — so these three guessed names are not just unconfirmed, they
// are absent from the input type. As predicted above, an unknown input-object field is a
// variable-coercion error that fails the whole request; ActiveOrdersTable.tsx no longer wires the
// search box or sortable column headers to this query as a result (its own comment explains why).
// The same never-validated caveat still applies to the `filters` entry shape and to HeaderExtras'
// tmxProfileId/channelId (the latter is also unread by the current OMS resolver). Note
// `ActiveOrdersFilter` never appears in the emitted document at all (it is nested inside the
// `$request` variable), so a wrong name there is also a coercion error.
//
// A top-level `userId` argument IS required here, despite order-management-svc's checked-in
// schema.graphqls declaring `activeOrders(headers: HeaderExtras, request: ActiveOrdersRequest!):
// ActiveOrdersResponse!` with no such argument (confirmed against the `develop` branch,
// 2026-09-22 — see concerns.instructions.md C6). A real request without `userId` fails; a real,
// working sample request has it set alongside `headers`/`request`, equal to `request.purchaserId`.
// This is a schema/actual-behavior mismatch (the deployed resolver accepts and needs an argument
// its own SDL doesn't declare) — do not "fix" this back out based on the SDL file alone.
import { params, shape, arrayOf, string, number, bool, id, customType } from '@thd-nucleus/data-sources';

export interface ActiveOrdersRequestFilter {
  name: string;
  value: string;
}

/**
 * The only values `{name: 'distributionDate'}` accepts — confirmed against the real request contract
 * (2026-08-12). Note the exact casing/spacing: title case with spaces ('Next 7 Days'), NOT the
 * camelCase preset slugs this repo uses internally ('next7Days'). They happen to match
 * `buildActiveOrdersDateRangePresets()`'s `label`s, but they are mapped explicitly via
 * ACTIVE_ORDERS_DATE_SENTINEL below rather than derived from labels, so relabeling a preset for UX
 * reasons cannot silently change what goes on the wire.
 */
export type ActiveOrdersDateSentinel = 'ALL' | 'Today' | 'Tomorrow' | 'Next 7 Days';

/** The only values `{name: 'distributionType'}` accepts — confirmed against the same contract. */
export type ActiveOrdersDistributionType = 'ALL' | 'DELIVERY' | 'PICKUP';

/**
 * Maps this repo's internal date-range preset values to the API's `distributionDate` sentinels.
 * `'custom'` is deliberately absent — the API has no way to express an arbitrary date range at all
 * (there are no startDate/endDate fields on ActiveOrdersRequest), so a custom pick falls back to
 * 'ALL'. See ActiveOrdersTable.tsx.
 */
export const ACTIVE_ORDERS_DATE_SENTINEL: Record<string, ActiveOrdersDateSentinel> = {
  today: 'Today',
  tomorrow: 'Tomorrow',
  next7Days: 'Next 7 Days'
};

export interface ActiveOrdersQueryVariables {
  headers?: {
    tmxProfileId?: string;
    channelId?: string;
  };
  request: {
    customerAccountId: string;
    purchaserId: string;
    pageSize: number;
    pageNumber: number;
    filters?: ActiveOrdersRequestFilter[];
  };
  userId: string;
}

export const activeOrdersDataModel = {
  activeOrders: params({
    headers: customType('HeaderExtras').shape({ tmxProfileId: string(), channelId: string() }),
    request: customType('ActiveOrdersRequest!').shape({
      customerAccountId: id().isRequired(),
      purchaserId: id().isRequired(),
      pageSize: number(),
      pageNumber: number(),
      filters: arrayOf(customType('ActiveOrdersFilter').shape({ name: string(), value: string() }))
    }),
    userId: id().isRequired()
  }).shape({
    customerAccountId: id(),
    userId: id(),
    totalCardCount: number(),
    totalDeliveryCount: number(),
    totalPickupCount: number(),
    activeDistributions: arrayOf(
      shape({
        distributionType: string(),
        orderNumber: string(),
        projectName: string(),
        poJobName: string(),
        status: string(),
        distributionDate: string(),
        distributionTime: string(),
        location: shape({
          type: string(),
          addressLine1: string(),
          city: string(),
          state: string(),
          zipCode: string(),
          storeId: string(),
          description: string()
        }),
        instructions: shape({
          securityCode: string(),
          signatureRequired: bool(),
          driverContact: string()
        }),
        lineItems: arrayOf(
          shape({
            name: string(),
            quantity: number(),
            unitPrice: number({ float: true }),
            totalPrice: number({ float: true })
          })
        )
      })
    )
  })
};
